import { Component, EventEmitter, Input, Output } from "@angular/core";
import { EstadoPeriodontograma, CaraPerio } from "app/models/periodontograma.interface";
import { EstadoOdontograma } from "app/models/odontograma.interface";
import { PeriodontogramaConfigService } from "app/services/periodontograma-config.service";
import { siluetaDiente, SiluetaDiente } from "app/shared/periodontograma/formas-dientes";

const TOOTH_AUSENTE = ["ausente", "extraccion_indicada", "no_erupcionado"];
const MM_PX = 4;
const CELL = 46;
const CEJ_OFFSET = 37;
const TOOTH_H = 72;
const REGLA_MM = [3, 6, 9, 12];

@Component({
  selector: "app-perio-banda",
  templateUrl: "./perio-banda.component.html",
  styleUrls: ["./perio-banda.component.scss"],
})
export class PerioBandaComponent {
  @Input() fila: string[] = [];
  @Input() cara: CaraPerio = "vestibular";
  @Input() estado: EstadoPeriodontograma = {};
  @Input() odontogramaEstado: EstadoOdontograma = {};
  @Input() mode: "edit" | "readonly" = "edit";

  @Output() dienteClick = new EventEmitter<{ fdi: string }>();

  constructor(private configService: PeriodontogramaConfigService) {}

  get dir(): number {
    return this.cara === "vestibular" ? 1 : -1;
  }

  get alto(): number {
    return 50 + 12 * MM_PX;
  }

  get ancho(): number {
    return this.fila.length * CELL;
  }

  get cejY(): number {
    return this.dir === 1 ? CEJ_OFFSET : this.alto - CEJ_OFFSET;
  }

  centroX(idx: number): number {
    return idx * CELL + CELL / 2;
  }

  sitioX(idx: number, i: number): number {
    return idx * CELL + CELL * (0.25 + 0.25 * i);
  }

  reglaLineas(): { y: number; mm: number }[] {
    return REGLA_MM.map((mm) => ({ y: this.cejY + this.dir * mm * MM_PX, mm }));
  }

  presente(fdi: string): boolean {
    const odo = this.odontogramaEstado?.[fdi];
    if (odo?.tooth && TOOTH_AUSENTE.includes(odo.tooth)) return false;
    const e = this.estado[fdi];
    return e ? e.presente !== false : true;
  }

  dientePath(fdi: string): string {
    return siluetaDiente(fdi).path;
  }

  dienteTransform(fdi: string, idx: number): string {
    const sil: SiluetaDiente = siluetaDiente(fdi);
    const s = TOOTH_H / sil.vbH;
    const tx = this.centroX(idx) - (s * sil.vbW) / 2;
    const ty = this.cejY - this.dir * s * sil.cejRatio * sil.vbH;
    return `translate(${tx},${ty}) scale(${s},${this.dir * s})`;
  }

  private yMargen(fdi: string, i: number): number | null {
    const m = this.estado[fdi]?.[this.cara]?.margen?.[i] ?? null;
    const s = this.estado[fdi]?.[this.cara]?.sondaje?.[i] ?? null;
    if (m === null && s === null) return null;
    return this.clamp(this.cejY + this.dir * (m ?? 0) * MM_PX);
  }

  private yBolsa(fdi: string, i: number): number | null {
    const m = this.estado[fdi]?.[this.cara]?.margen?.[i] ?? null;
    const s = this.estado[fdi]?.[this.cara]?.sondaje?.[i] ?? null;
    if (m === null && s === null) return null;
    return this.clamp(this.cejY + this.dir * ((m ?? 0) + (s ?? 0)) * MM_PX);
  }

  private clamp(y: number): number {
    return Math.max(2, Math.min(this.alto - 2, y));
  }

  puntosMargen(): string {
    return this.puntos((fdi, i) => this.yMargen(fdi, i));
  }

  puntosBolsa(): string {
    return this.puntos((fdi, i) => this.yBolsa(fdi, i));
  }

  private puntos(fy: (fdi: string, i: number) => number | null): string {
    const pts: string[] = [];
    this.fila.forEach((fdi, idx) => {
      if (!this.presente(fdi)) return;
      [0, 1, 2].forEach((i) => {
        const y = fy(fdi, i);
        if (y === null) return;
        pts.push(`${this.sitioX(idx, i)},${y}`);
      });
    });
    return pts.join(" ");
  }

  poligonoBolsa(): string {
    const margen = this.puntosMargen();
    const bolsa = this.puntosBolsa();
    if (!margen || !bolsa) return "";
    const bolsaRev = bolsa.split(" ").reverse().join(" ");
    return `${margen} ${bolsaRev}`;
  }

  sangradoPuntos(): { x: number; y: number }[] {
    const out: { x: number; y: number }[] = [];
    this.fila.forEach((fdi, idx) => {
      if (!this.presente(fdi)) return;
      [0, 1, 2].forEach((i) => {
        if (this.estado[fdi]?.[this.cara]?.sangrado?.[i]) {
          const y = this.yMargen(fdi, i);
          if (y !== null) out.push({ x: this.sitioX(idx, i), y });
        }
      });
    });
    return out;
  }

  readonly colorBolsa = "#1976d2";
  readonly colorMargen = "#e53935";

  colorSangrado(): string {
    return this.configService.config.colores.sangrado;
  }

  onDiente(fdi: string): void {
    if (this.mode === "readonly" || !this.presente(fdi)) return;
    this.dienteClick.emit({ fdi });
  }
}
